import { getTheme, IStackItemStyles, ThemeProvider } from '@fluentui/react';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { default as Activity } from 'models/activity';
import { IOwner } from 'models/owner';
import { useContext, useEffect, useState } from 'react';
import { getValidPageContent, getWebPartIncludingType } from 'services/Graph/graphServicePage';
import {
  IHorizontalSection,
  IHorizontalSectionColumn,
  ISitePage,
  IStandardWebPart,
  ITextWebPart,
  IVerticalSection,
  IWebPart,
  IWebPartType,
} from 'services/Graph/SharepointInterfaces';
import logger from 'services/Logging/logService';
import { cloneObject } from 'utils/object';
import { PageHeaderPage } from './SharePointPageHeaders';
import { SharePointTextWebPart } from './WebParts/SharePointTextWebPart';

//Custom SharePoint CSS
import './SharePointPageRenderer.css';

import AppContext from 'App/AppContext';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { ContentScaler } from 'components/Utils/ContentScaler';
import { useWidth } from 'globalHooks';
import ResourceLink from 'models/resourceLink';
import { darkTheme, lightTheme } from 'themes/runtime/globalThemes-v8';
import { getPageEmphasisColor } from './SharePointPageRenderHelpers';
import { SharePointImageWebPart } from './WebParts/SharePointImageWebPart';

interface ISitePageContentProps {
  link: ResourceLink;
  page: ISitePage | undefined;
  enableLinks?: boolean;
  previewLinks?: boolean;
  fitToWidth?: boolean;
  activity?: Activity;
  owner?: IOwner;
  hideHeader?: boolean;
  siteId?: string;
  disableScrollContainer?: boolean;
  forceLightMode?: boolean;
  disableHeaderGradient?: boolean;
  // When provided (page editor mode), text web parts render through this callback instead of the read-only
  // SharePointTextWebPart. Lets the editor reuse this renderer's traversal, content validation
  // (getValidPageContent) and section theming instead of maintaining a forked copy.
  editableTextWebPart?: (webPart: ITextWebPart, page: ISitePage) => JSX.Element | null;
}

//Include the default SharePoint font sizes and line height as in the .sharePointPage class
const webPartStackItemStyles: IStackItemStyles = { root: { padding: 20, fontSize: 18, lineHeight: 1.1 } };

const SharePointPageRenderer = (props: ISitePageContentProps) => {
  const appContext = useContext(AppContext);
  const [pageContent, setPageContent] = useState<ISitePage | undefined>(undefined);
  const { ref: contentRef, width } = useWidth<HTMLDivElement>();
  const theme = getTheme();
  const useDarkMode = props.forceLightMode ? false : appContext.useDarkMode;

  useEffect(() => {
    if (props.page) {
      const clonedPage = cloneObject(props.page);
      setPageContent(getValidPageContent(clonedPage));
    }
  }, [props.page]);

  const renderWebPart = (page: ISitePage, item: IWebPart): string | JSX.Element | JSX.Element[] | null => {
    let output: string | JSX.Element | JSX.Element[] | null = null;
    const part = getWebPartIncludingType(item);

    switch (part?.internalType) {
      case IWebPartType.text:
        try {
          output = props.editableTextWebPart ? (
            props.editableTextWebPart(part as ITextWebPart, page)
          ) : (
            <SharePointTextWebPart
              webPart={part as ITextWebPart}
              enableLinks={props.enableLinks}
              previewLinks={props.previewLinks}
              pageContent={pageContent}
              siteId={props.siteId}
            />
          );
        } catch {
          output = null;
        }
        break;
      case IWebPartType.image:
        output = <SharePointImageWebPart webPart={part as IStandardWebPart} page={page} />;
        break;
      default:
        logger.debug(`Unsupported internal web part type: ${item.internalType}`);
        break;
    }

    return output;
  };

  const renderColumn = (page: ISitePage, column: IHorizontalSectionColumn) => {
    return (
      <IPStack>
        {column.webparts.map((wp) => {
          return (
            <IPStack.Item key={`sp-column-webpart-${wp.id}`} styles={webPartStackItemStyles}>
              {renderWebPart(page, wp)}
            </IPStack.Item>
          );
        })}
      </IPStack>
    );
  };

  const renderHorizontalSection = (page: ISitePage, section: IHorizontalSection) => {
    const emphasisColor = getPageEmphasisColor(section.emphasis, theme);

    return (
      <IPStack
        className="sharePointPage-horizontalSection"
        key={`sp-horizontal-section-${section.id}`}
        horizontal
        verticalAlign="stretch"
        styles={{ root: { backgroundColor: emphasisColor } }}
      >
        {section.columns.map((col) => {
          // Calculate width percentage based on the 12-column grid
          const colWidthPercent = ((col.width ?? 12) / 12) * 100;

          return (
            <IPStack.Item
              className="sharePointPage-horizontalColumn"
              key={`sp-column-${col.id}`}
              styles={{ root: { width: `${colWidthPercent}%`, overflow: 'hidden', display: 'flex' } }}
            >
              {renderColumn(page, col)}
            </IPStack.Item>
          );
        })}
      </IPStack>
    );
  };

  const renderVerticalSection = (page: ISitePage, vertical: IVerticalSection) => {
    const emphasisColor = getPageEmphasisColor(vertical.emphasis, theme);

    return (
      <IPStack
        className="sharePointPage-verticalSection"
        verticalFill
        styles={{ root: { backgroundColor: emphasisColor, paddingRight: 20, height: '100%' } }}
      >
        <ThemeProvider applyTo="element" theme={vertical.emphasis === 'strong' ? darkTheme : theme}>
          <IPStack verticalFill styles={{ root: { backgroundColor: emphasisColor, height: '100%' } }}>
            {/* Re-apply the background */}
            {vertical.webparts.map((wp) => (
              <IPStack.Item key={`sp-vert-webpart-${wp.id}`} styles={webPartStackItemStyles}>
                {renderWebPart(page, wp)}
              </IPStack.Item>
            ))}
          </IPStack>
        </ThemeProvider>
      </IPStack>
    );
  };

  //
  // Main render
  //
  if (!props.page || !pageContent) return null;

  return (
    <IPStack verticalFill={!props.disableScrollContainer}>
      <ScrollableStackItem disableScroll={props.disableScrollContainer === true}>
        <ThemeProvider applyTo="element" theme={useDarkMode ? darkTheme : lightTheme}>
          {!props.hideHeader && (
            <PageHeaderPage
              link={props.link}
              page={props.page}
              activity={props.activity}
              owner={props.owner}
              forceLightMode={props.forceLightMode}
              disableGradient={props.disableHeaderGradient}
            />
          )}
          <IPStack className="sharePointPage" verticalFill={!props.disableScrollContainer}>
            <div ref={contentRef}>
              <ContentScaler width={width} disabled={!props.fitToWidth}>
                <IPStack.Item>
                  <IPStack className="sharePointPage-mainLayout" horizontal verticalAlign="stretch">
                    <IPStack.Item grow>
                      {pageContent.canvasLayout?.horizontalSections?.map((section) => {
                        return renderHorizontalSection(pageContent, section);
                      })}
                    </IPStack.Item>
                    {/* Optional vertical section on the right */}
                    {pageContent.canvasLayout?.verticalSection && (
                      <IPStack.Item
                        className="sharePointPage-verticalSectionHost"
                        styles={{ root: { width: '25%', marginLeft: 20, display: 'flex' } }}
                      >
                        {renderVerticalSection(pageContent, pageContent.canvasLayout.verticalSection)}
                      </IPStack.Item>
                    )}
                  </IPStack>
                </IPStack.Item>
              </ContentScaler>
            </div>
          </IPStack>
        </ThemeProvider>
      </ScrollableStackItem>
    </IPStack>
  );
};

export default SharePointPageRenderer;
